import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import useTranslation from 'next-translate/useTranslation';

import { getBeErrorMessage } from '~/common/utils/getBeErrorMessage';
import type { CreateGroupInvitesPayload } from '~/services/marketplace/marketplace-service.service.api';
import { createGroupInvites } from '~/services/marketplace/marketplace-service.service.api';

import { GroupEntriesQueryKeys } from '../constants/query';

export const useCreateGroupInvites = ({
  userId,
  onSuccess,
}: {
  userId?: string;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation('group');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateGroupInvitesPayload) => createGroupInvites(payload),
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries({
        queryKey: GroupEntriesQueryKeys.entryViewsForUser(userId ?? ''),
      });
      showNotification({ message: t('notifications.invitesSent'), color: 'green' });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const beMessage = getBeErrorMessage(error);
      if (beMessage) {
        showNotification({ message: beMessage, color: 'red' });
        return;
      }
      if (axios.isAxiosError(error) && !error.response) {
        showNotification({ message: t('notifications.unknownOutcome'), color: 'red' });
        return;
      }
      showNotification({ message: t('notifications.createFailed'), color: 'red' });
    },
  });
};
